import { useEffect, useRef, useState } from 'react';

/**
 * Inline click-to-edit title — the same contentEditable behavior as the experience builder's
 * title bar (placeholder when empty, save on blur/Enter, revert on Escape or if left empty,
 * paste-as-plaintext). `textClassName` sets the type scale so callers can match their heading.
 */
export default function EditableTitle({
  value, onSave, placeholder, textClassName = 'text-lg font-semibold', ariaLabel,
}: {
  value: string;
  onSave: (value: string) => void;
  placeholder: string;
  textClassName?: string;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastSaved = useRef(value);
  const focusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!value);

  // Seed the contentEditable once on mount; subsequent value changes flow through the effect below.
  useEffect(() => { if (ref.current) ref.current.textContent = value; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    lastSaved.current = value;
    setIsEmpty(!value);
    if (ref.current && !focusedRef.current) ref.current.textContent = value;
  }, [value]);

  function handleBlur() {
    focusedRef.current = false;
    setIsFocused(false);
    const text = ref.current?.textContent?.trim() ?? '';
    setIsEmpty(!text);
    if (!text) { if (ref.current) ref.current.textContent = lastSaved.current; setIsEmpty(!lastSaved.current); return; }
    if (text !== lastSaved.current) { lastSaved.current = text; onSave(text); }
  }
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === 'Escape') { if (ref.current) ref.current.textContent = lastSaved.current; e.currentTarget.blur(); }
  }
  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain').replace(/\n/g, ' '));
  }

  return (
    <div className={`relative min-w-0 ${isFocused ? 'flex-1' : ''}`}>
      {isEmpty && !isFocused && (
        <span className={`pointer-events-none absolute inset-0 select-none px-2 py-0.5 text-ws-muted ${textClassName}`}>{placeholder}</span>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role='textbox'
        tabIndex={0}
        aria-label={ariaLabel ?? placeholder}
        onFocus={() => { focusedRef.current = true; setIsFocused(true); }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onInput={() => setIsEmpty(!(ref.current?.textContent?.trim()))}
        className={`rounded-md border-b-2 border-transparent bg-black/[0.04] px-2 py-0.5 text-ws-text transition-colors hover:bg-black/[0.07] focus:border-ws-primary focus:bg-black/[0.04] focus:outline-none dark:bg-white/[0.06] dark:hover:bg-white/[0.10] dark:focus:bg-white/[0.06] ${textClassName} ${isFocused ? 'whitespace-normal break-words' : 'overflow-hidden text-ellipsis whitespace-nowrap'}`}
      />
    </div>
  );
}
